import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Download, Database, Trash2 } from "lucide-react";
import {
  deletePortalAccount,
  downloadPortalExport,
  signOutPatient,
} from "../../application/portalAccount";
import { usePortalRecords } from "../../application/usePortalAuth";
import { clearActivePatientId } from "../../application/portalContext";

export default function PatientDataPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeRecord } = usePortalRecords();
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Whose processing this page is about. With more than one professional the
  // patient has more than one controller, and an unlabelled page would imply
  // one export covers them all.
  const controller = activeRecord?.therapist_name ?? null;
  const controllerCenter = activeRecord?.center_name ?? null;

  const handleDeleteAccount = async () => {
    setDeleting(true);
    setError(null);
    try {
      await deletePortalAccount();
      clearActivePatientId();
      await signOutPatient();
      navigate("/portal/login", { replace: true });
    } catch {
      setError(t("portal.account.delete.error", "We could not delete your account just now."));
      setDeleting(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    setError(null);
    try {
      const data = await downloadPortalExport();
      const blob = new Blob([data], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "alchely-my-data.json";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError(t("portal.export.error"));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div className="max-w-2xl mx-auto flex flex-col gap-6">
        {/* Page title */}
        <div>
          <h1 className="text-2xl font-bold text-content-primary">
            {t("portal.export.title")}
          </h1>
          <p className="text-sm text-content-tertiary mt-1">
            {t("portal.export.description")}
          </p>
        </div>

        {/* Content card */}
        <div className="glass-effect glass-border border rounded-2xl shadow-sm p-6 flex flex-col gap-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-brand-50 dark:bg-brand-900/20 flex items-center justify-center shrink-0">
              <Database className="w-6 h-6 text-brand-500" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                {t("portal.export.downloadButton")}
              </p>
              <p className="text-xs text-content-tertiary mt-0.5">
                {t("portal.export.formatHint", "Formato JSON")}
              </p>
            </div>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <button
            onClick={handleDownload}
            disabled={downloading}
            className="self-start flex items-center gap-2 rounded-xl bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white font-medium px-6 py-2.5 transition-colors"
          >
            <Download className="w-4 h-4" />
            {downloading ? t("portal.export.downloading") : t("portal.export.downloadButton")}
          </button>

          {/* Who holds what the patient is about to download: the footnote of
              this card, not a line floating between two of them. */}
          {controller && (
            <p className="border-t border-gray-100 dark:border-white/5 pt-4 text-xs text-content-tertiary">
              {t("portal.data.controllerNotice", {
                defaultValue:
                  "This panel and its data are held by {{controller}}. Each professional keeps their own record of you.",
                controller: controllerCenter ? `${controller} · ${controllerCenter}` : controller,
              })}
            </p>
          )}
        </div>

        <div className="rounded-2xl border border-red-200 dark:border-red-500/20 p-6 flex flex-col gap-4">
          <div>
            <h2 className="text-sm font-medium text-gray-800 dark:text-gray-100">
              {t("portal.account.delete.title", "Delete my portal account")}
            </h2>
            <p className="text-xs text-content-tertiary mt-1">
              {t(
                "portal.account.delete.whatIsRemoved",
                "Removes your sign-in and your access to every panel.",
              )}
            </p>
            <p className="text-xs text-content-tertiary mt-1">
              {t(
                "portal.account.delete.whatIsKept",
                "Your clinical records stay with each professional treating you, who keeps them for as long as the law requires. To have those erased, ask them directly.",
              )}
            </p>
          </div>

          {confirmingDelete ? (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-medium px-5 py-2.5 text-sm transition-colors"
              >
                {deleting
                  ? t("portal.account.delete.deleting", "Deleting…")
                  : t("portal.account.delete.confirm", "Yes, delete my account")}
              </button>
              <button
                onClick={() => setConfirmingDelete(false)}
                disabled={deleting}
                className="rounded-xl px-5 py-2.5 text-sm text-content-secondary hover:bg-surface-secondary transition-colors"
              >
                {t("common.cancel", "Cancel")}
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmingDelete(true)}
              className="self-start flex items-center gap-2 rounded-xl border border-red-300 dark:border-red-500/30 text-red-600 dark:text-red-400 font-medium px-5 py-2.5 text-sm hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              {t("portal.account.delete.title", "Delete my portal account")}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
